import { useEffect, useRef, useState } from "react";
import { BroadcastTransport } from "./BroadcastTransport";
import { WebSocketTransport } from "./WebSocketTransport";
import type { MpClient, MpEvent, MpPositionTick, MpRoomState } from "./types";
import type { AvatarConfig } from "@shared/types";

/**
 * Pick the transport based on environment:
 *   - localhost / 127.0.0.1 → BroadcastChannel (multi-tab dev testing)
 *   - everything else → WebSocket → Cloudflare RaceRoom Durable Object
 *
 * `?mp=ws` or `?mp=bc` in the URL forces a transport (handy for LAN testing).
 */
function pickTransport(): MpClient {
  if (typeof window !== "undefined") {
    const force = new URLSearchParams(window.location.search).get("mp");
    if (force === "ws") return new WebSocketTransport();
    if (force === "bc") return new BroadcastTransport();
    const host = window.location.hostname;
    const isLocal = host === "localhost" || host === "127.0.0.1" || host.endsWith(".local");
    return isLocal ? new BroadcastTransport() : new WebSocketTransport();
  }
  return new BroadcastTransport();
}

export interface RemotePosition extends MpPositionTick {
  id: string;
  updatedAt: number;
}

export interface UseMpRoomResult {
  client: MpClient | null;
  state: MpRoomState | null;
  positions: Map<string, RemotePosition>;
  error: string | null;
}

interface JoinOpts {
  code: string;
  asHost: boolean;
  me: { id: string; displayName: string; avatarConfig: AvatarConfig };
  lessonId?: string;
  seed?: number;
  visibility?: "open" | "private";
}

/**
 * Singleton active-room cache — survives React route changes so the
 * WebSocket *doesn't* tear down when navigating /battle → /battle/play.
 * That used to drop the connection for 200–500ms, which the user saw as
 * "sync drops out 5 seconds in" (right after the host pressed Start).
 *
 * Multiple useMpRoom callers (lobby AND in-game) share the same room
 * when their (code, me.id) match. When the LAST subscriber unmounts, the
 * client lingers for `LEAVE_GRACE_MS` to let a quick route transition
 * re-attach without rebuilding the connection.
 */
interface ActiveRoom {
  client: MpClient;
  joinKey: string;
  positions: Map<string, RemotePosition>;
  state: MpRoomState | null;
  error: string | null;
  subscribers: Set<() => void>;
  off: () => void;
  pendingCloseTimer: number | null;
  rafId: number;
  dirty: boolean;
}

let activeRoom: ActiveRoom | null = null;
const LEAVE_GRACE_MS = 2000;

function notifyAll() {
  if (!activeRoom) return;
  for (const cb of activeRoom.subscribers) cb();
}

function initRoom(opts: JoinOpts): ActiveRoom {
  const client = pickTransport();
  const positions = new Map<string, RemotePosition>();
  const joinKey = `${opts.code}::${opts.me.id}`;
  const room: ActiveRoom = {
    client,
    joinKey,
    positions,
    state: null,
    error: null,
    subscribers: new Set(),
    off: () => {},
    pendingCloseTimer: null,
    rafId: 0,
    dirty: false,
  };
  const off = client.on("event", (e: MpEvent) => {
    if (e.type === "state") {
      // Drop out-of-order snapshots: the server stamps a monotonic `rev` on
      // every state, so if the network reorders two state messages we ignore
      // the older one (prevents a brief roster/phase rollback mid-race).
      const nextRev = (e.state as { rev?: number }).rev;
      const curRev = (room.state as { rev?: number } | null)?.rev;
      if (typeof nextRev === "number" && typeof curRev === "number" && nextRev < curRev) {
        return;
      }
      room.state = e.state;
      notifyAll();
    } else if (e.type === "tick") {
      positions.set(e.from, {
        id: e.from,
        ...e.tick,
        updatedAt: performance.now(),
      });
      room.dirty = true;
    } else if (e.type === "leave") {
      positions.delete(e.from);
      room.dirty = true;
    } else if (e.type === "error") {
      room.error = e.message;
      notifyAll();
    }
  });
  room.off = off;
  // RAF pump coalesces position-tick changes into one React render per frame.
  const pump = () => {
    if (!activeRoom || activeRoom !== room) return;
    room.rafId = requestAnimationFrame(pump);
    if (room.dirty) {
      room.dirty = false;
      notifyAll();
    }
  };
  room.rafId = requestAnimationFrame(pump);
  // Kick off the actual join. Errors and the success-state both flow through
  // the event listener above, so we only need the promise for failure paths.
  client.join(opts).then((s) => { room.state = s; notifyAll(); }).catch((err) => {
    room.error = (err as Error).message ?? "Could not join battle";
    notifyAll();
  });
  return room;
}

function destroyRoom(room: ActiveRoom) {
  cancelAnimationFrame(room.rafId);
  room.off();
  try { room.client.leave(); } catch { /* ignore */ }
  if (activeRoom === room) activeRoom = null;
}

export function useMpRoom(opts: JoinOpts | null): UseMpRoomResult {
  // Force re-render whenever the singleton notifies.
  const [, setTick] = useState(0);
  const subscriberRef = useRef<() => void>(() => setTick((n) => (n + 1) & 0x7fffffff));

  useEffect(() => {
    if (!opts) return;
    const joinKey = `${opts.code}::${opts.me.id}`;

    if (activeRoom && activeRoom.joinKey === joinKey) {
      // Reuse existing connection — perfect for /battle → /battle/play.
      if (activeRoom.pendingCloseTimer != null) {
        clearTimeout(activeRoom.pendingCloseTimer);
        activeRoom.pendingCloseTimer = null;
      }
    } else {
      // Different room (or no room). Force-close the old one immediately.
      if (activeRoom) {
        if (activeRoom.pendingCloseTimer != null) clearTimeout(activeRoom.pendingCloseTimer);
        destroyRoom(activeRoom);
      }
      activeRoom = initRoom(opts);
    }

    const sub = subscriberRef.current;
    activeRoom.subscribers.add(sub);
    // Pull current state immediately so the new subscriber doesn't render with
    // null while waiting for the next event.
    sub();

    return () => {
      if (!activeRoom) return;
      activeRoom.subscribers.delete(sub);
      // Defer the real disconnect — if another route mounts within the grace
      // window with the same joinKey, it'll re-attach without dropping WS.
      if (activeRoom.subscribers.size === 0 && activeRoom.pendingCloseTimer == null) {
        const room = activeRoom;
        activeRoom.pendingCloseTimer = window.setTimeout(() => {
          if (room.subscribers.size === 0) destroyRoom(room);
        }, LEAVE_GRACE_MS);
      }
    };
  }, [opts?.code, opts?.asHost, opts?.me.id, opts?.lessonId, opts?.seed]);

  return {
    client: activeRoom?.client ?? null,
    state: activeRoom?.state ?? null,
    positions: activeRoom?.positions ?? new Map(),
    error: activeRoom?.error ?? null,
  };
}
